/**
 * @author Hiroki Moto
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import originalMoment from 'moment';
import { extendMoment } from 'moment-range';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import keys from 'ramda/src/keys';

const moment = extendMoment(originalMoment);

const ExposureChart = ({ company, date }) => {
    const exposureChart = useRef(null);
    const [selectedProduct, setSelectedProduct] = useState(null);

    useEffect(() => {
        const { interactionData } = generateReviewsData();
        const minDate = new Date(date.start.year(), date.start.month(), date.start.date());
        const maxDate = new Date(date.end.year(), date.end.month(), date.end.date());

        // Create Exposure chart instance
        let y = am4core.create('exposure-chart', am4charts.XYChart);
        y.paddingRight = 20;
        // Add data
        y.data = interactionData;
        // Create axes
        let exposureDateAxis = y.xAxes.push(new am4charts.DateAxis());
        exposureDateAxis.renderer.minGridDistance = 50;
        exposureDateAxis.renderer.grid.template.location = 0.5;
        exposureDateAxis.startLocation = 0.5;
        exposureDateAxis.endLocation = 0.5;
        exposureDateAxis.baseInterval = { timeUnit: 'month', count: 1 };
        exposureDateAxis.min = minDate.getTime();
        exposureDateAxis.max = maxDate.getTime();
        exposureDateAxis.skipEmptyPeriods = true;
        // Create value axis
        let exposureValueAxis = y.yAxes.push(new am4charts.ValueAxis());
        exposureValueAxis.renderer.opposite = true;
        exposureValueAxis.min = 0;
        exposureValueAxis.max = 300;
        // Create series
        let exposureSeries1 = createSeries(y, 'impressions', 'Impressions', '#f00');
        let exposureSeries2 = createSeries(y, 'views', 'Views', '#0f0');
        let exposureSeries3 = createSeries(y, 'clicks', 'Clicks', '#00f');
        exposureSeries2.hidden = true;
        exposureSeries3.hidden = true;
        exposureChart.current = y;

        return () => {
            y.dispose();
        };
    }, []);

    useEffect(() => {
        setSelectedProduct(company);
    }, [company]);

    useEffect(() => {
        if (selectedProduct) {
            const { interactionData } = generateReviewsData();
            exposureChart.current.data = interactionData;
        }
    }, [selectedProduct]);

    const generateReviewsData = () => {
        const productData = selectedProduct || company;
        if (productData?.rating_analytics?.data) {
            const { interaction_analytics } = productData.rating_analytics.data;
            const interactionData = keys(interaction_analytics).map((dateKey) => {
                const { clicks, impressions, views } = interaction_analytics[dateKey].month;
                const keyStr = dateKey.split('_');
                const yearStr = keyStr[1].substring(0, 4);
                const monthStr = keyStr[1].substring(4);
                return { date: new Date(yearStr, monthStr, 1), clicks, impressions, views };
            });
            return { interactionData };
        }
        return { interactionData: [] };
    };

    const createSeries = (chart, field, name, color) => {
        let series = chart.series.push(new am4charts.LineSeries());
        series.dataFields.valueY = field;
        series.dataFields.dateX = 'date';
        series.name = name;
        series.tooltipText = '{dateX}: [b]{valueY}[/]';
        series.stroke = am4core.color(color);
        series.strokeWidth = 1;
        series.tensionX = 0.8;
        series.connect = true;
        return series;
    };

    const handleSelectExposure = (e) => {
        if (exposureChart?.current?.series) {
            const currentSeries = exposureChart.current.series.getIndex(e[0]);
            exposureChart.current.series.each((series) => {
                if (series != currentSeries) {
                    series.hide();
                }
            });
            currentSeries.show();
        }
    };

    return <div className="visibility-review" id="exposure-chart" />;
};

ExposureChart.propTypes = {
    company: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        products: PropTypes.array,
        stats: PropTypes.shape({
            product_count: PropTypes.number,
            popularity_score: PropTypes.number,
            rating_recommended_avg: PropTypes.number,
            rating_count: PropTypes.number,
            rating_dealership_count: PropTypes.number,
        }),
    }),
    date: PropTypes.any,
};

ExposureChart.defaultProps = {
    company: {
        id: 0,
        name: '',
        products: [],
        stats: {
            product_count: 0,
            popularity_score: 0,
            rating_recommended_avg: 0,
            rating_count: 0,
            rating_dealership_count: 0,
        },
    },
    date: moment.range(moment().startOf('month').format('YYYY-MM-DD'), moment().endOf('month').format('YYYY-MM-DD')),
};

export default ExposureChart;
